import { z } from "zod";
import { paginationQuery, searchParam } from "./pagination";
import type { OrganizationTypeRefWithLabel } from "./organization-types";
import { type OrganizationTypeCount, organizationTypeFilterParam } from "./responsible-organizations";

/**
 * `/sub-agencies` — the committee / agency a card is issued under (Venue Ops,
 * Security, Medical…). A flat global list on purpose: the predecessor's
 * RO↔sub-agency assignment table is deliberately not carried over until
 * registration proves it needs one.
 *
 * Since 0016 a sub-agency may belong to an organisation type (the Register
 * picker for that type — step 3 of the chain asks
 * `/sub-agencies/options?organization_type_id=`) and name a sport (a national
 * federation presets the card's sport).
 */

export const SUB_AGENCY_SORT_KEYS = ["name", "organization_type", "created_at"] as const;
export type SubAgencySortKey = (typeof SUB_AGENCY_SORT_KEYS)[number];

export const listSubAgenciesQuery = paginationQuery.extend({
	search: searchParam.optional(),
	/** A type's uuid, or `none` for sub-agencies with no type. */
	organization_type_id: organizationTypeFilterParam.optional(),
	sort: z.enum(SUB_AGENCY_SORT_KEYS).optional(),
	dir: z.enum(["asc", "desc"]).optional(),
});
export type ListSubAgenciesQuery = z.infer<typeof listSubAgenciesQuery>;

/** `GET /sub-agencies/options?organization_type_id=` — that type's rows only. */
export const subAgencyOptionsQuery = z.object({
	organization_type_id: z.string().uuid().optional(),
});
export type SubAgencyOptionsQuery = z.infer<typeof subAgencyOptionsQuery>;

/**
 * A sport CODE — a `sports.code` of the games (AT, AQ) — not an id: sports
 * are rows per games and one federation serves both. Trimmed first (bug 5),
 * then stored uppercase.
 */
export const SPORT_CODE_MAX = 10;
export const sportCodeField = z
	.string()
	.trim()
	.min(1)
	.max(SPORT_CODE_MAX)
	.transform((v) => v.toUpperCase());

/** One row of `GET /sub-agencies`. */
export interface SubAgencyListItem {
	sub_agency_id: string;
	name: string;
	/** The organisation type whose picker offers this row; `null` = not set. */
	organization_type: OrganizationTypeRefWithLabel | null;
	/** Picking this sub-agency presets the card's sport. */
	sport_code: string | null;
	/** Cards issued under this agency. An agency in use cannot be deleted. */
	cards_count: number;
	/** User-group scope rows (`user_group_scopes`) naming this agency. */
	scopes_count: number;
	created_at: string;
	updated_at: string;
}

/** `GET /sub-agencies/options` — for `<select>` elements. */
export interface SubAgencyOption {
	sub_agency_id: string;
	name: string;
	organization_type: OrganizationTypeRefWithLabel | null;
	sport_code: string | null;
}

/** `GET /sub-agencies/stats` — the stats strip, in one statement. */
export interface SubAgencyStats {
	total: number;
	/** Sub-agencies at least one card is issued under. */
	in_use: number;
	unused: number;
	/** Every organisation type (zero counts included), then the no-type bucket. */
	by_organization_type: OrganizationTypeCount[];
}

// ── Write requests ──────────────────────────────────────────────────────────

export interface SubAgencyFormMessages {
	nameRequired: string;
}

export const enSubAgencyFormMessages: SubAgencyFormMessages = {
	nameRequired: "Enter a name",
};

/**
 * `organization_type_id` and `sport_code` are optional in the FORM: a form
 * that does not render them leaves them `undefined`, and the update helper
 * then leaves the columns alone instead of clearing them. `""` clears.
 */
export function makeSubAgencyForm(m: SubAgencyFormMessages = enSubAgencyFormMessages) {
	return z.object({
		name: z.string().trim().min(1, m.nameRequired).max(200),
		organization_type_id: z.string().uuid().or(z.literal("")).optional(),
		sport_code: sportCodeField.or(z.literal("")).optional(),
	});
}
export const subAgencyForm = makeSubAgencyForm();
export type SubAgencyForm = z.infer<typeof subAgencyForm>;

export interface CreateSubAgencyBody {
	name: string;
	organization_type_id?: string;
	sport_code?: string;
}

/** `null` clears a field; omitted leaves it alone. */
export interface UpdateSubAgencyBody {
	name?: string;
	organization_type_id?: string | null;
	sport_code?: string | null;
	/** Headline for this change in the Activity feed. */
	reason?: string;
}

export const toCreateSubAgencyBody = (f: SubAgencyForm): CreateSubAgencyBody => ({
	name: f.name,
	...(f.organization_type_id ? { organization_type_id: f.organization_type_id } : {}),
	...(f.sport_code ? { sport_code: f.sport_code } : {}),
});

/** Only what actually changed, so the audit row's `changed_fields` names the edit. */
export function toUpdateSubAgencyBody(f: SubAgencyForm, before: SubAgencyListItem): UpdateSubAgencyBody {
	const body: UpdateSubAgencyBody = {};
	if (f.name !== before.name) body.name = f.name;
	const diffNullable = (next: string, prev: string | null) => (next === "" ? (prev === null ? undefined : null) : next === prev ? undefined : next);
	if (f.organization_type_id !== undefined) {
		const type = diffNullable(f.organization_type_id, before.organization_type?.id ?? null);
		if (type !== undefined) body.organization_type_id = type;
	}
	if (f.sport_code !== undefined) {
		const sport = diffNullable(f.sport_code, before.sport_code);
		if (sport !== undefined) body.sport_code = sport;
	}
	return body;
}
